/**
 * Client Persona Library API service.
 *
 * Wraps the Phase 1 endpoints exposed under /api/admin/personas/client/*.
 */

import apiClient from './api';

// 'training' uses the lecturer-scoped routes under /api/training/personas (6 Oct 2026);
// anything else the admin library routes.
const personaBase = (scope) => (scope === 'training' ? '/training/personas' : '/admin/personas/client');

/**
 * Faceted + semantic search over the persona library.
 *
 * @param {object} payload  { filters, pagination, orgScope }
 * @param {string} token
 * @returns {Promise<{ results, total, facetCounts, meta }>}
 */
export const searchPersonas = async (payload, token, scope) => {
  try {
    const res = await apiClient.post(`${personaBase(scope)}/search`, payload, token);
    return res.data;
  } catch (err) {
    console.error('searchPersonas failed:', err);
    throw err.response?.data || err;
  }
};

export const clonePersona = async (sourceKey, body, token) => {
  try {
    const res = await apiClient.post(`/admin/personas/client/${encodeURIComponent(sourceKey)}/clone`, body, token);
    return res.data;
  } catch (err) {
    console.error('clonePersona failed:', err);
    throw err.response?.data || err;
  }
};

export const getPersonaUsage = async (key, token, scope) => {
  try {
    const res = await apiClient.get(`${personaBase(scope)}/${encodeURIComponent(key)}/usage`, token);
    return res.data;
  } catch (err) {
    console.error('getPersonaUsage failed:', err);
    throw err.response?.data || err;
  }
};

export const getPersonaVariants = async (key, token, scope) => {
  try {
    const res = await apiClient.get(`${personaBase(scope)}/${encodeURIComponent(key)}/variants`, token);
    return res.data;
  } catch (err) {
    console.error('getPersonaVariants failed:', err);
    throw err.response?.data || err;
  }
};

export const getLibraryHealth = async (params, token) => {
  try {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    const res = await apiClient.get(`/admin/personas/client/library-health${qs}`, token);
    return res.data;
  } catch (err) {
    console.error('getLibraryHealth failed:', err);
    throw err.response?.data || err;
  }
};

export const getSimilarPersonas = async (key, limit, token, scope) => {
  try {
    const res = await apiClient.get(`${personaBase(scope)}/${encodeURIComponent(key)}/similar?limit=${limit || 5}`, token);
    return res.data;
  } catch (err) {
    console.error('getSimilarPersonas failed:', err);
    throw err.response?.data || err;
  }
};

export const regeneratePersonaEmbedding = async (key, token) => {
  try {
    const res = await apiClient.post(`/admin/personas/client/${encodeURIComponent(key)}/regenerate-embedding`, {}, token);
    return res.data;
  } catch (err) {
    console.error('regeneratePersonaEmbedding failed:', err);
    throw err.response?.data || err;
  }
};

/**
 * Convenience wrapper around the shared service-groups endpoint, returning
 * just the fields the persona library needs (id, name, groupType).
 */
export const listServiceGroupsForPicker = async (token) => {
  try {
    const res = await apiClient.get('/service-groups', token);
    const groups = res.data?.serviceGroups || res.data || [];
    return groups.map(g => ({
      _id: g._id,
      name: g.name,
      slug: g.slug,
      groupType: g.groupType,
      isPlatformLibrary: g.groupType === 'platform_authoring'
    }));
  } catch (err) {
    console.error('listServiceGroupsForPicker failed:', err);
    throw err.response?.data || err;
  }
};

export const customiseScenarioPersona = async (scenarioId, body, token) => {
  try {
    // Training route: lecturers in the scenario's group and admins (6 Oct 2026).
    const res = await apiClient.post(`/training/scenarios/${encodeURIComponent(scenarioId)}/customise-persona`, body, token);
    return res.data;
  } catch (err) {
    console.error('customiseScenarioPersona failed:', err);
    throw err.response?.data || err;
  }
};

// A persona's editable fields, and whether the caller may edit it (training scenario editor).
export const getTrainingPersona = async (personaId, token) => {
  try {
    const res = await apiClient.get(`/training/personas/by-id/${encodeURIComponent(personaId)}`, token);
    return res.data;
  } catch (err) {
    console.error('getTrainingPersona failed:', err);
    throw err.response?.data || err;
  }
};

export const updateTrainingPersona = async (personaId, body, token) => {
  try {
    const res = await apiClient.put(`/training/personas/by-id/${encodeURIComponent(personaId)}`, body, token);
    return res.data;
  } catch (err) {
    console.error('updateTrainingPersona failed:', err);
    throw err.response?.data || err;
  }
};

// Admin: groups a persona can be shared with for training, and the sharing setting itself.
export const getTrainingGroupsForSharing = async (token) => {
  try {
    const res = await apiClient.get('/admin/personas/client/training-groups', token);
    return res.data;
  } catch (err) {
    console.error('getTrainingGroupsForSharing failed:', err);
    throw err.response?.data || err;
  }
};

export const setPersonaTrainingSharing = async (key, body, token) => {
  try {
    const res = await apiClient.put(`/admin/personas/client/${encodeURIComponent(key)}/training-sharing`, body, token);
    return res.data;
  } catch (err) {
    console.error('setPersonaTrainingSharing failed:', err);
    throw err.response?.data || err;
  }
};
